import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskAssessment = () => {
  const [selectedScenario, setSelectedScenario] = useState('current');
  const [riskMetrics, setRiskMetrics] = useState({
    totalExposure: 125000,
    hedgedAmount: 98250,
    exposureRatio: 78.6,
    valueAtRisk: 245000,
    maxDrawdown: 180000,
    correlationRisk: 0.72,
    liquidityRisk: 'Low',
    concentrationRisk: 'Medium'
  });

  const [scenarios, setScenarios] = useState([
    {
      id: 'current',
      name: 'Current Market',
      description: 'Based on current market conditions',
      priceChange: 0,
      volatility: 15.2,
      impact: 0,
      probability: 100
    },
    {
      id: 'bull_market',
      name: 'Bull Market',
      description: 'EUA prices rise 25% over 3 months',
      priceChange: 25,
      volatility: 22.8,
      impact: 312500,
      probability: 35
    },
    {
      id: 'bear_market',
      name: 'Bear Market',
      description: 'EUA prices fall 20% over 3 months',
      priceChange: -20,
      volatility: 28.5,
      impact: -250000,
      probability: 25
    },
    {
      id: 'high_volatility',
      name: 'High Volatility',
      description: 'Increased market uncertainty',
      priceChange: 0,
      volatility: 35.0,
      impact: -125000,
      probability: 40
    },
    {
      id: 'regulatory_change',
      name: 'Regulatory Shock',
      description: 'New EU regulations affect supply',
      priceChange: 40,
      volatility: 45.0,
      impact: 500000,
      probability: 15
    }
  ]);

  const [riskBreakdown, setRiskBreakdown] = useState([
    {
      category: 'Price Risk',
      current: 245000,
      potential: 380000,
      mitigation: 'Hedging Strategy',
      status: 'Medium'
    },
    {
      category: 'Liquidity Risk',
      current: 45000,
      potential: 120000,
      mitigation: 'Diversified Brokers',
      status: 'Low'
    },
    {
      category: 'Counterparty Risk',
      current: 25000,
      potential: 85000,
      mitigation: 'Credit Monitoring',
      status: 'Low'
    },
    {
      category: 'Operational Risk',
      current: 15000,
      potential: 50000,
      mitigation: 'System Redundancy',
      status: 'Low'
    },
    {
      category: 'Regulatory Risk',
      current: 180000,
      potential: 450000,
      mitigation: 'Compliance Monitoring',
      status: 'High'
    }
  ]);

  const [hedgeEffectiveness, setHedgeEffectiveness] = useState([
    { period: 'Q4 2024', effectiveness: 92.5, variance: 2.1 },
    { period: 'Q1 2025', effectiveness: 89.8, variance: 3.4 },
    { period: 'Q2 2025', effectiveness: 87.2, variance: 4.2 },
    { period: 'Q3 2025', effectiveness: 85.6, variance: 5.1 }
  ]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getRiskColor = (status) => {
    switch (status.toLowerCase()) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getImpactColor = (impact) => {
    if (impact > 0) return 'text-success';
    if (impact < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const selectedScenarioData = scenarios.find(s => s.id === selectedScenario);

  return (
    <div className="space-y-6">
      {/* Risk Overview */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Risk Assessment Dashboard</h2>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} strokeWidth={2} className="mr-2" />
              Export Report
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="RefreshCw" size={16} strokeWidth={2} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">
              {formatCurrency(riskMetrics.totalExposure)}
            </div>
            <div className="text-sm text-muted-foreground">Total Exposure</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              {formatCurrency(riskMetrics.hedgedAmount)}
            </div>
            <div className="text-sm text-muted-foreground">Hedged Amount</div>
          </div>
          
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              riskMetrics.exposureRatio >= 80 ? 'text-success' :
              riskMetrics.exposureRatio >= 60 ? 'text-warning' : 'text-error'
            }`}>
              {riskMetrics.exposureRatio.toFixed(1)}%
            </div>
            <div className="text-sm text-muted-foreground">Hedge Ratio</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-error">
              {formatCurrency(riskMetrics.valueAtRisk)}
            </div>
            <div className="text-sm text-muted-foreground">Value at Risk (95%)</div>
          </div>
        </div>
      </div>

      {/* Scenario Analysis */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Scenario Analysis</h3>
          <div className="flex items-center space-x-2">
            {scenarios.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => setSelectedScenario(scenario.id)}
                className={`px-3 py-1 rounded text-sm font-medium transition-maritime ${
                  selectedScenario === scenario.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {scenario.name}
              </button>
            ))}
          </div>
        </div>

        {selectedScenarioData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">{selectedScenarioData.name}</h4>
              <p className="text-sm text-muted-foreground mb-4">{selectedScenarioData.description}</p>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Price Change:</span>
                  <span className={`text-sm font-medium ${getImpactColor(selectedScenarioData.priceChange)}`}>
                    {selectedScenarioData.priceChange > 0 ? '+' : ''}{selectedScenarioData.priceChange}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Volatility:</span>
                  <span className="text-sm font-medium text-foreground">
                    {selectedScenarioData.volatility}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Probability:</span>
                  <span className="text-sm font-medium text-foreground">
                    {selectedScenarioData.probability}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">P&L Impact:</span>
                  <span className={`text-sm font-bold ${getImpactColor(selectedScenarioData.impact)}`}>
                    {selectedScenarioData.impact !== 0 ? formatCurrency(selectedScenarioData.impact) : 'Neutral'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Risk Mitigation Actions</h4>
              <div className="space-y-3">
                {selectedScenarioData.priceChange > 20 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium text-foreground">Increase Hedge Ratio</div>
                      <div className="text-muted-foreground">Consider hedging additional 15% exposure</div>
                    </div>
                  </div>
                )}
                {selectedScenarioData.volatility > 30 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="TrendingUp" size={16} className="text-warning mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium text-foreground">Diversify Strategy</div>
                      <div className="text-muted-foreground">Spread purchases across multiple timeframes</div>
                    </div>
                  </div>
                )}
                {selectedScenarioData.probability < 30 && (
                  <div className="flex items-start space-x-2">
                    <Icon name="Eye" size={16} className="text-accent mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium text-foreground">Monitor Closely</div>
                      <div className="text-muted-foreground">Set up alerts for early warning signals</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Breakdown */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Risk Category Breakdown</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Risk Category</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Current VaR</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Potential VaR</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Mitigation</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {riskBreakdown.map((risk, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/30">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    {risk.category}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono">
                    {formatCurrency(risk.current)}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-mono text-error">
                    {formatCurrency(risk.potential)}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">
                    {risk.mitigation}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(risk.status)}`}>
                      {risk.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hedge Effectiveness */}
      <div className="bg-card rounded-lg border shadow-maritime-sm p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Hedge Effectiveness Forecast</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="space-y-4">
              {hedgeEffectiveness.map((period, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">{period.period}</span>
                    <span className={`text-sm font-bold ${
                      period.effectiveness >= 90 ? 'text-success' :
                      period.effectiveness >= 80 ? 'text-warning' : 'text-error'
                    }`}>
                      {period.effectiveness.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 mb-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        period.effectiveness >= 90 ? 'bg-success' :
                        period.effectiveness >= 80 ? 'bg-warning' : 'bg-error'
                      }`}
                      style={{ width: `${period.effectiveness}%` }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Variance: ±{period.variance.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3">Effectiveness Insights</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <Icon name="TrendingDown" size={16} className="text-warning mt-0.5" />
                <div>
                  <div className="font-medium text-foreground">Declining Effectiveness</div>
                  <div className="text-muted-foreground">Hedge effectiveness decreases over time due to basis risk</div>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                <div>
                  <div className="font-medium text-foreground">Rebalancing Required</div>
                  <div className="text-muted-foreground">Consider quarterly hedge rebalancing to maintain effectiveness</div>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Icon name="Target" size={16} className="text-success mt-0.5" />
                <div>
                  <div className="font-medium text-foreground">Optimal Range</div>
                  <div className="text-muted-foreground">Target effectiveness above 85% for adequate risk coverage</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAssessment;